import { AppRequestError } from "@pilotfish/app-api";
import { endSession } from "@pilotfish/core";
import { useStytch, useStytchUser } from "@stytch/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchMemberships,
  type Membership,
  type Memberships,
  openFamilies,
} from "../api/memberships";
import { AccountChip, signedInEmail } from "../components/AccountChip";
import { Card } from "../components/Card";
import { Frame } from "../components/Frame";
import { ErrorState, Loading, NoFamily, SignedOut } from "../components/states";
import { API_ORIGIN, signInUrl, startFamilyUrl } from "../config";
import { PanelProvider } from "../panelContext";
import { Scopes } from "./Scopes";

/**
 * The shell: **is anybody signed in, which families are theirs, and what do we show when the
 * answer to either is "none"** (issue #301).
 *
 * Three gates, in this order, and each one is a state rather than an error:
 *
 * 1. **The SDK is still deciding.** Two flags, not one: `<StytchProvider>` defaults
 *    `assumeHydrated` to true, so `isInitialized` is already true on the first render, and
 *    `fromCache` is what says the value came from storage with a refresh in flight. Gating on
 *    `isInitialized` alone announces "you're not signed in" mid-refresh — on a surface about to
 *    show family data, that reads as being signed out rather than as a page still loading
 *    (PR #319 review, one issue earlier).
 * 2. **No session.** Hand them to the login app, which is the only place sign-in happens.
 * 3. **No family they can open.** Show the server's own guidance, never a sentence of ours.
 *
 * **The credential is read per request, and this component is where that could most easily go
 * wrong.** `api.token` is a *function* closed over the Stytch client, so every call reads
 * whatever JWT the browser holds at that moment. It is deliberately not `useState`, not a
 * `useMemo` over `getTokens()`, and not passed down as a string: Stytch stamps a five-minute
 * `exp` and refreshes in the background, so a token captured at mount is stale twelve times
 * over in an hour — ADR 0021 decision 3's named failure, "a control panel that works for five
 * minutes after sign-in".
 */
export function Panel({ apiOrigin = API_ORIGIN }: { apiOrigin?: string | null }) {
  const stytch = useStytch();
  const { user, isInitialized, fromCache } = useStytchUser<false>();

  // The client, reachable from a closure without being a dependency of one. `api` below must
  // be **referentially stable** — it is in the effect's dependency list, so an object rebuilt
  // on every render would re-run the load on every render, forever. Depending on the identity
  // of a vendor hook's return value to prevent that is a promise the vendor never made.
  const client = useRef(stytch);
  client.current = stytch;

  const api = useMemo(
    () => ({
      origin: apiOrigin ?? "",
      // Read at call time. See the header — this closure is the "never cache" rule.
      token: () => client.current.session.getTokens()?.session_jwt ?? null,
    }),
    [apiOrigin],
  );

  const [state, setState] = useState<Load>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);
  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  const signedIn = isInitialized && !fromCache && user !== null;

  /**
   * The panel state every screen below reads, **memoised**, and for the same reason `api` is.
   * A context value rebuilt on every render re-runs every consumer's effect, and #302's screens
   * are made of effects that fetch — so an unstable value here is a request loop over family
   * data, in code that looks correct at both ends. `email` is a string rather than the SDK's
   * user object precisely so it can be compared by value.
   */
  const memberships = state.status === "ready" ? state.memberships : null;
  const families = useMemo(
    () => (memberships === null ? NO_FAMILIES : openFamilies(memberships)),
    [memberships],
  );
  const email = user === null ? null : signedInEmail(user);
  const signOut = useCallback(() => void endSession(client.current), []);
  const panel = useMemo(
    () => ({ api, families, email, reload, signOut }),
    [api, families, email, reload, signOut],
  );

  // `attempt` is not read in the effect — it IS the trigger. "Check again" on the no-family
  // card increments it, and that is what re-runs a load whose other inputs are unchanged by
  // definition (the same session, the same api). Removing it, as the rule's fix suggests,
  // makes that button do nothing.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `attempt` is the re-run trigger.
  useEffect(() => {
    if (!signedIn) return;
    let live = true;
    setState({ status: "loading" });
    fetchMemberships(api)
      .then((memberships) => {
        if (live) setState({ status: "ready", memberships });
      })
      .catch((error: unknown) => {
        if (live) setState({ status: "failed", code: codeOf(error) });
      });
    // Ignoring a resolved promise for a component that has been replaced — React has no
    // cancellable fetch, and a `setState` after unmount is a warning about nothing.
    return () => {
      live = false;
    };
  }, [signedIn, api, attempt]);

  if (!isInitialized || fromCache) {
    return (
      <Card>
        <Loading label="Getting things ready…" />
      </Card>
    );
  }

  if (user === null) return <SignedOut signInUrl={signInUrl()} startFamilyUrl={startFamilyUrl()} />;

  if (state.status === "loading") {
    return (
      <Card>
        <Loading label="Finding your family…" />
      </Card>
    );
  }

  if (state.status === "failed") {
    // A session the server would not accept is the signed-out state, not a fault: the local
    // session outlived what Stytch will vouch for. #305 measured what that is worth at minute
    // 61, and this branch turned out to be the one that closes the gap. The SDK notices an
    // expiry within three minutes of it (its refresh interval), and in the meantime the panel
    // still renders a signed-in shell — so a family who acts inside that window arrives here
    // instead, `session_required` because the cookie expires with the session so there is no
    // token to send, or `session_invalid` if one does reach the server.
    if (state.code === "session_required" || state.code === "session_invalid") {
      // **No create offer on this branch, and it is the one place the two differ** (PR #366
      // review). Reaching here means the browser held a Stytch user and the SERVER rejected the
      // session, so whoever is reading demonstrably has an account — and following the offer
      // would start them a SECOND household, whose name #360 then reserves permanently. The
      // branch above keeps it: that state cannot tell a first-timer from a deliberate sign-out,
      // and #305 chose not to try. A first-timer never arrives here, because somebody signing in
      // through the panel with no household lands on `NoFamily`.
      return <SignedOut signInUrl={signInUrl()} startFamilyUrl={null} />;
    }
    return (
      <Card>
        <ErrorState
          title="We couldn't load your family"
          body="Something went wrong at our end. Try again in a moment — nothing you've told Boone is affected."
        />
      </Card>
    );
  }

  if (families.length === 0) {
    const { nextStep } = state.memberships;
    // `nextStep` is non-null whenever no family is open — the server decides that, and this
    // branch is what a disagreement would look like. It is an error rather than a sentence of
    // our own, because inventing joining guidance here is the one thing this card must not do.
    return nextStep === null ? (
      <Card>
        <ErrorState
          title="We couldn't work out which family to show you"
          body="Try again in a moment — nothing you've told Boone is affected."
        />
      </Card>
    ) : (
      // The chrome a family-less member keeps and the chrome they lose (#293): the account
      // chip stays, because they ARE signed in and this is where a wrong account gets
      // noticed; the switcher and the nav go, because there is nothing to switch between.
      <Frame account={<AccountChip email={email} onSignOut={signOut} />}>
        <NoFamily nextStep={nextStep} onRetry={reload} />
      </Frame>
    );
  }

  return (
    <PanelProvider value={panel}>
      <Scopes />
    </PanelProvider>
  );
}

/** One frozen empty array, so "no families yet" is not a new value on every render. */
const NO_FAMILIES: readonly Membership[] = [];

type Load =
  | { status: "loading" }
  | { status: "ready"; memberships: Memberships }
  | { status: "failed"; code: string };

/** The server's refusal code, or a stand-in for anything that was not one. */
function codeOf(error: unknown): string {
  return error instanceof AppRequestError ? error.code : "unknown_error";
}
